import type * as React from 'react';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { motion, stagger, useAnimate, useMotionValue } from 'framer-motion';
import { rgba } from 'polished';
import type Prism from 'prismjs';
import styled from 'styled-components';

import { OutputArrowIcon } from '@/icons/Icons';
import { splitChars, useSyncAnimationToMotionValue } from '@/utils/animation';
import { combineRefs } from '@/utils/react';
import { highlightSyntax } from '@/utils/syntax-highlighting';

export const Cell = styled(motion.div)`
  --font-size--default: min(13px, 1.8vw);
  --font-size--small: min(9px, 1.2vw);
  --padding: 9px;
  --background-color: 29 29 37;

  position: relative;
  border-radius: 3px;
  border: 1px solid #353548;
  background: rgb(var(--background-color));
  color: #e4e6ec;
  transition: border-color 0.2s;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: ${({ theme }) => rgba(theme.borderColor.MUTED, 0.1)} 0px 0px
      10px 0px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &[data-active='true'] {
    border-color: ${({ theme }) => rgba(theme.appUI.magic.secondaryColor, 0.4)};

    &::after {
      opacity: 1;
    }
  }
`;

const RunButtonWrapper = styled(motion.div)`
  position: absolute;
  top: 11px;
  right: 11px;
`;

export function RunButton(
  props: React.ComponentPropsWithoutRef<typeof RunButtonWrapper>
) {
  return (
    <RunButtonWrapper {...props}>
      <svg
        fill="none"
        height="20"
        viewBox="0 0 31 20"
        width="31"
        xmlns="http://www.w3.org/2000/svg">
        <g opacity="0.8">
          <rect
            fill="#A8ADBC"
            fillOpacity="0.12"
            height="20"
            rx="2"
            width="31"
          />
          <path
            d="M7.74391 5.8902L12.9054 9.33118L12.8954 9.35118L13.2627 9.59602C13.4185 9.69995 13.5 9.84513 13.5 10C13.5 10.1549 13.4185 10.3 13.2627 10.404L12.8954 10.6488L12.9054 10.6688L7.74392 14.1098L7.73392 14.0898L7.26265 14.404C7.16171 14.4713 7.07788 14.5 7 14.5C6.72614 14.5 6.5 14.2739 6.5 14V6C6.5 5.72614 6.72614 5.5 7 5.5C7.07788 5.5 7.16171 5.52873 7.26265 5.59603L7.73391 5.9102L7.74391 5.8902Z"
            stroke="#A8ADBC"
          />
          <g opacity="0.6">
            <path
              clipRule="evenodd"
              d="M22 10.8371L19.2652 8.10229L18.7349 8.63262L22 11.8978L25.2652 8.63262L24.7349 8.10229L22 10.8371Z"
              fill="#A8ADBC"
              fillRule="evenodd"
            />
          </g>
        </g>
      </svg>
    </RunButtonWrapper>
  );
}

export const Label = styled(motion.div)`
  position: absolute;
  padding: 3px;
  top: -10px;
  left: var(--padding);
  overflow: hidden;
  line-height: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  background-image: linear-gradient(
    180deg,
    transparent 0%,
    rgb(var(--background-color) / 0.6) 66%,
    rgb(var(--background-color))
  );
`;

export const Code = styled.code`
  position: relative;
  width: 100%;
  overflow-x: hidden;
  display: block;
  padding: 2em 16px;
  counter-reset: line;
  font-size: var(--font-size--default);
  line-height: 1.5714285714;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  color: rgb(228, 230, 236);
  padding-right: 16px;

  .line {
    counter-increment: line;
  }

  .line::before {
    content: counter(line);
    display: inline-block;
    width: 12px;
    margin-right: 20px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #b1b6c4;
    opacity: 0.5;
  }
`;

const Pre = styled.pre`
  position: relative;

  &[data-variant='animated'] .char {
    opacity: 0;
  }
`;

export const FormattedCode = forwardRef(function FormattedCode(
  {
    children,
    language = 'sql',
    typingProgress,
    preformattedCode,
    ...props
  }: {
    children?: string;
    language?: Parameters<typeof Prism.highlight>[2];
    preformattedCode?: string;
    typingProgress?: ReturnType<typeof useMotionValue<number>>;
  } & Omit<JSX.IntrinsicElements['pre'], 'children'>,
  ref: React.ForwardedRef<HTMLPreElement>
) {
  const _progress = useMotionValue(1);
  const codeRef = useRef<HTMLElement>(null);
  const [scope, animate] = useAnimate();
  const [controller, setController] = useState<ReturnType<
    typeof animate
  > | null>(null);

  const code = useMemo(() => {
    if (preformattedCode) return '';
    // @ts-expect-error todo
    return highlightSyntax(children, language);
  }, [children, language, preformattedCode]);

  useLayoutEffect(() => {
    if (!typingProgress) return;

    const codeEl = codeRef.current;
    if (!codeEl) return;

    const alreadySplit = codeEl.querySelector('.char');
    if (alreadySplit !== null) return;

    splitChars(codeEl);
  }, [code, typingProgress]);

  // Wouldn't normally set this asynch, but dom nodes need to be mounted before
  // animation can initialize with selector.
  useEffect(() => {
    setController(
      animate([
        [
          '.char',
          {
            opacity: [0, 1],
            filter: ['blur(3px)', 'blur(0px)']
          },
          { duration: 0.1, delay: stagger(0.005), ease: 'linear' }
        ]
      ])
    );
  }, [animate]);

  useSyncAnimationToMotionValue(controller, typingProgress || _progress);

  const variant =
    (typingProgress || _progress).get() < 1 ? 'animated' : 'default';

  return (
    <Pre {...props} ref={combineRefs(scope, ref)} data-variant={variant}>
      <Code
        ref={codeRef}
        dangerouslySetInnerHTML={{ __html: preformattedCode || code }}
      />
    </Pre>
  );
});

const OutputWrapper = styled(motion.div)`
  position: absolute;
  bottom: 0;
  padding: 0 var(--padding);
`;

const OutputInner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  transform: translateY(calc(6px + 100%));
`;

const OutputPill = styled(motion.div)`
  padding: 0px 4px;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
  line-height: ${({ theme }) => theme.lineHeight.SMALL};

  color: rgb(98, 217, 107);
  background-color: rgba(67, 191, 77, 0.1);
`;

export function Output({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof OutputWrapper>) {
  return (
    <OutputWrapper {...props}>
      <OutputInner>
        <OutputArrowIcon />
        <OutputPill>{children}</OutputPill>
      </OutputInner>
    </OutputWrapper>
  );
}

export const Table = styled.table`
  --border: 1px solid #353548;

  border-collapse: collapse;
  width: 100%;
  font-family: ${({ theme }) => theme.fontFamily.MONO};

  * {
    font-size: min(13px, 1.8vw);
    line-height: 1.5714285714;
  }

  thead {
    border-top: var(--border);
    background-color: #14141c;

    th {
      text-align: left;
    }
  }

  tbody {
    th {
      padding-right: 20px;
      text-align: right;
      font-variant-numeric: tabular-nums;
      opacity: 0.5;
    }
  }

  tr {
    border-top: var(--border);
  }

  th {
    font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
    /* padding: 0px 2px; */
    color: #b1b6c4;
  }

  td {
    text-align: left;
    padding: 2px 8px;
  }

  tfoot {
    text-align: right;
    /* padding: 0px 8px; */
    border-top: var(--border);

    td {
      text-align: right;
      white-space: nowrap;
    }

    td:first-of-type {
      width: auto;
    }
  }
`;
